/**
 * Assistant API (#765, spec §2.2, §8.1)
 *
 * Five endpoints behind the edge-api assistant router. The same paths serve
 * standalone and embedded mode — the embedded fetch rewrites `/api/v1/*` to
 * `/api/v1/embedded/*`, where the router is mounted a second time. Every
 * response is a `{ data }` envelope validated with the SSOT schemas, so a
 * turn that fails `assistantAnswerSchema` is rejected here, not rendered.
 */

import { z } from 'zod/v4';
import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import {
  assistantAnswerSchema,
  assistantEntityNamesResponseSchema,
  assistantQuotaResponseSchema,
  assistantThreadSchema,
  type AssistantAnswer,
  type AssistantEntityNamesFilters,
  type AssistantAskRequest,
  type AssistantQuotaResponse,
  type AssistantThread,
  type AssistantThreadsQuery,
} from '@aica/api-types/schemas';

const answerEnvelope = z.object({ data: assistantAnswerSchema });
const threadsEnvelope = z.object({ data: z.array(assistantThreadSchema) });
const quotaEnvelope = z.object({ data: assistantQuotaResponseSchema });

export class AssistantApi extends BaseApiClient {
  /**
   * `POST /assistant/ask`. `idempotencyKey` is a uuid minted per submit
   * (§2.2): a replay with the same key — a double click, a resend from the
   * thread — returns the existing turn instead of spending a second question.
   *
   * The fetch layer is told NOT to repeat this call on its own (`maxRetries`
   * 0): §2.2 — «мутация `ask` не автоповторяется никогда». A ceiling refusal
   * or a 503 would otherwise be re-sent up to three times with backoff and
   * reach the user seven seconds late with the same verdict (Codex P2,
   * round 3). The 401 token refresh and the CSRF refresh are outside that
   * count and still apply.
   */
  async ask(
    body: AssistantAskRequest,
    idempotencyKey: string,
    /** The caller's deadline: an aborted request is a failure with no verdict. */
    signal?: AbortSignal
  ): Promise<AssistantAnswer> {
    const { data } = await this.validatedRequest(
      API_ENDPOINTS.ASSISTANT_ASK,
      answerEnvelope,
      {
        method: 'POST',
        body: JSON.stringify(body),
        extraHeaders: { 'Idempotency-Key': idempotencyKey },
        ...(signal ? { signal } : {}),
      },
      0
    );
    return data;
  }

  /** `GET /assistant/turns/:turnId` — the polling read of an in-flight turn. */
  async getTurn(turnId: string): Promise<AssistantAnswer> {
    const { data } = await this.validatedRequest(
      API_ENDPOINTS.ASSISTANT_TURN(turnId),
      answerEnvelope
    );
    return data;
  }

  /** `GET /assistant/threads?scope=…` — the viewer's conversations about one subject. */
  async listThreads(query: AssistantThreadsQuery): Promise<AssistantThread[]> {
    const { data } = await this.validatedRequest(
      `${API_ENDPOINTS.ASSISTANT_THREADS}${this.buildQuery({ ...query })}`,
      threadsEnvelope
    );
    return data;
  }

  /**
   * `GET /assistant/entity-names?q=&filters=` — customer names for the
   * whole-base filter, as stored, among the records the viewer may see under
   * the question's other filters (#765 Ф3, #1361).
   */
  async entityNames(
    q: string,
    filters?: AssistantEntityNamesFilters,
    signal?: AbortSignal
  ): Promise<string[]> {
    const query = filters ? { q, filters: JSON.stringify(filters) } : { q };
    const { data } = await this.validatedRequest(
      `${API_ENDPOINTS.ASSISTANT_ENTITY_NAMES}${this.buildQuery(query)}`,
      assistantEntityNamesResponseSchema,
      signal ? { signal } : undefined
    );
    return data.names;
  }

  /** `GET /assistant/quota` — today's remaining questions, without spending one. */
  async getQuota(): Promise<AssistantQuotaResponse> {
    const { data } = await this.validatedRequest(
      API_ENDPOINTS.ASSISTANT_QUOTA,
      quotaEnvelope
    );
    return data;
  }
}
